import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { XMarkIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { baseAxios } from "../../apis";
import Drawer from "../commons/Drawer";
import { useBotDemo } from "../../context/botDemoContext";
import { useBalanceQuery } from "../../hooks/useBotQueries";
import { validateBotSettings } from "../../utils/validateBotSettings";

// Components (Keeping original Demo paths)
import XenoarcComponent from "../forward-testing-bot/create-bot-setting/XenoarcComponent";
import DCAComponent from "../forward-testing-bot/create-bot-setting/DCAComponent";
import RebalanceComponent from "../forward-testing-bot/create-bot-setting/RebalanceComponent";
import { normalizeBotName } from "../../utils";
import { BOT_ACTIVATE_STEPS } from "../../context/botContext";

const STRATEGIES = {
  REBALANCE: "rebalance",
  GRID: "grid",
  XENO_ARC: "xeno arc",
  XENO_V3: "xeno bot v3",
  DCA: "dca",
};

const BotSettingsSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="grid grid-cols-1 gap-4">
      <div className="space-y-2">
        <div className="h-4 bg-gray-100 rounded-full w-1/3"></div>
        <div className="h-12 bg-gray-200 rounded-xl"></div>
      </div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-100 rounded-full w-1/4"></div>
      <div className="h-12 bg-gray-200 rounded-xl"></div>
    </div>
    <div className="pt-4 space-y-4">
      <div className="h-44 bg-gray-200 rounded-xl"></div>
    </div>
  </div>
);

// Adapted for Demo API
const useCoinPairsQueryDemo = (selectedExchange) => {
  const exchangeId = selectedExchange?.id;
  return useQuery({
    queryKey: ["coinPairsDemo", exchangeId],
    queryFn: async () => {
      const { data } = await baseAxios.get(
        `/api/demo/get_coins?exchange_id=${exchangeId}`,
      );
      return data.data.map((dta) => ({
        id: dta?.id,
        pair: `${dta?.coin}/${dta?.base_currency}`,
        price: dta?.coin_price || "0",
        coin: dta.coin,
        base_currency: dta.base_currency,
      }));
    },
    enabled: !!exchangeId,
  });
};

const DrawerActivateBot2Demo = ({
  open,
  onClose,
  onNext,
  onBack,
  isEdit,
  selectedBot,
  formRebalance,
  setFormRebalance,
  formXenoV2,
  setFormXenoV2,
  formXenoGrid,
  setFormXenoGrid,
  formDCA,
  setFormDCA,
  isLoading,
}) => {
  const {
    exchanges,
    fetchExchangesDemo,
    loadingGetExchange: loadingExchanges,
    selectedSetting,
    botDetail,
    openDialogBot,
  } = useBotDemo();

  const [selectedExchange, setSelectedExchange] = useState(null);
  const [selectedPair, setSelectedPair] = useState(null);
  const [errors, setErrors] = useState({});

  // Store the initial capital when bot data is first loaded (for edit mode)
  const [initialBotCapital, setInitialBotCapital] = useState(0);

  // Pass 'true' as 3rd argument to indicate Demo mode if your hook supports it
  const { data: getBalance, isLoading: loadingBalance } = useBalanceQuery(
    selectedPair,
    selectedExchange,
    true,
  );

  const { data: pairs = [], isLoading: loadingPairs } =
    useCoinPairsQueryDemo(selectedExchange);
  const [additionalTradeSize, setAdditionalTradeSize] = useState(0);
  const [additionalCapital, setAdditionalCapital] = useState(0);

  const userBalance = getBalance?.demo_balance || 0;

  const { activeForm, setActiveForm } = useMemo(() => {
    const strategy = selectedBot?.bot_name?.toLowerCase();
    switch (strategy) {
      case STRATEGIES.GRID:
        return { activeForm: formXenoGrid, setActiveForm: setFormXenoGrid };
      case STRATEGIES.XENO_ARC:
        return { activeForm: formXenoV2, setActiveForm: setFormXenoV2 };
      case STRATEGIES.DCA:
        return { activeForm: formDCA, setActiveForm: setFormDCA };
      case STRATEGIES.XENO_V3:
        return { activeForm: {}, setActiveForm: () => {} };
      default:
        return { activeForm: formRebalance, setActiveForm: setFormRebalance };
    }
  }, [
    selectedBot,
    formRebalance,
    formXenoGrid,
    formXenoV2,
    formDCA,
    setFormRebalance,
    setFormXenoGrid,
    setFormXenoV2,
    setFormDCA,
  ]);

  // Capture initial capital ONCE when edit mode opens
  useEffect(() => {
    if (isEdit && activeForm?.settings && initialBotCapital === 0) {
      const strategy = selectedBot?.bot_name?.toLowerCase();
      let capital = 0;

      switch (strategy) {
        case STRATEGIES.GRID:
          capital = Number(activeForm.settings.amount) || 0;
          break;
        case STRATEGIES.XENO_ARC:
          capital = Number(activeForm.settings.capital) || 0;
          break;
        case STRATEGIES.DCA:
          capital = Number(activeForm.settings.capital) || 0;
          break;
        case STRATEGIES.REBALANCE:
          capital = Number(activeForm.settings.capital) || 0;
          break;
        default:
          capital = 0;
      }

      if (capital > 0) {
        setInitialBotCapital(capital);
      }
    }
  }, [isEdit, activeForm, selectedBot, initialBotCapital]);

  // Calculate total available balance (available + initial capital in edit mode)
  const totalAvailableBalance = useMemo(() => {
    if (isEdit) {
      return userBalance + initialBotCapital;
    }
    return userBalance;
  }, [isEdit, userBalance, initialBotCapital]);

  // Fetch exchanges on mount if missing
  useEffect(() => {
    if (!exchanges || exchanges.length === 0) {
      fetchExchangesDemo();
    }
  }, [exchanges, fetchExchangesDemo]);

  // Reset pair when exchange changes
  useEffect(() => {
    if (selectedExchange) {
      setSelectedPair(null);
    }
  }, [selectedExchange]);

  // Clear errors when selection changes
  useEffect(() => {
    if (selectedPair || selectedExchange) {
      setErrors({});
    }
  }, [selectedExchange, selectedPair]);

  // Reset initialBotCapital when dialog closes
  useEffect(() => {
    if (!open) {
      setInitialBotCapital(0);
    }
  }, [open]);

  useEffect(() => {
    if (
      openDialogBot !== BOT_ACTIVATE_STEPS.STEP_2_EDIT &&
      openDialogBot !== BOT_ACTIVATE_STEPS.STEP_3_EDIT
    ) {
      setAdditionalCapital(0);
      setAdditionalTradeSize(0);
    }
  }, [openDialogBot]);

  // Populate form data from props (Edit Mode)
  useEffect(() => {
    const exchangeNameFromProp = activeForm?.exchange_name || null;
    const coinNameFromProp = activeForm?.coin_name || null;

    if (exchangeNameFromProp && exchanges.length > 0) {
      const preselectedExchange = exchanges.find(
        (ex) => ex.name.toLowerCase() === exchangeNameFromProp.toLowerCase(),
      );
      if (preselectedExchange) {
        setSelectedExchange(preselectedExchange);

        // Note: pairs might load asynchronously, this logic relies on pairs being populated
        if (coinNameFromProp && pairs.length > 0) {
          const preselectedPair = pairs.find(
            (p) => p.pair === coinNameFromProp,
          );
          setSelectedPair(preselectedPair || null);
        }
      }
    }
  }, [activeForm, exchanges, pairs]);

  const isFormValid = useMemo(() => {
    const strategy = selectedBot?.bot_name?.toLowerCase();
    const s = activeForm?.settings;
    if (!s) return false;
    const commonValidation = isEdit
      ? true
      : !!selectedPair?.id && !!selectedBot?.id;
    switch (strategy) {
      case STRATEGIES.GRID:
        return (
          commonValidation &&
          s.amount > 0 &&
          s.trade_size > 0 &&
          s.grid_width > 0 &&
          s.grid_width_exit > 0 &&
          s.number_of_grids > 0
        );
      case STRATEGIES.XENO_ARC:
        return (
          commonValidation &&
          s.capital > 0 &&
          s.amount > 0 &&
          s.buy_flg_return > 0 &&
          s.grid_width > 0 &&
          s.grid_width_exit > 0 &&
          s.exit_flg_return > 0 &&
          s.number_of_grids > 0
        );
      case STRATEGIES.REBALANCE:
        return (
          !!selectedPair?.id &&
          !!selectedBot?.id &&
          s.capital > 0 &&
          s.allocations.length > 0
        );
      case STRATEGIES.DCA:
        return (
          commonValidation &&
          s.capital > 0 &&
          s.amount > 0 &&
          s.max_order_count > 0 &&
          s.price_change_percentage > 0 &&
          s.take_profit_percentage > 0
        );
      default:
        return true;
    }
  }, [selectedBot, activeForm, selectedPair, isEdit]);

  const handleNextClick = () => {
    const s = activeForm?.settings;
    const strategy = selectedBot?.bot_name?.toLowerCase();

    const newErrors = validateBotSettings({
      strategy,
      settings: s,
      selectedSetting,
      minimumOrder: getBalance?.minimum_order,
      baseCurrency: selectedPair?.base_currency,
      isEdit,
      botDetail,
      additionalCapital,
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setActiveForm((prev) => {
      if (!prev?.settings) return prev;

      const s = prev.settings;
      let calculatedSettings = {};

      if (isEdit) {
        switch (strategy) {
          case STRATEGIES.GRID:
            const originalAmountGrid = Number(botDetail?.setting?.amount) || 0;
            const originalTradeSize =
              Number(botDetail?.setting?.trade_size) || 0;

            // Both are pure deltas starting from 0
            calculatedSettings.amount =
              originalAmountGrid + Number(additionalCapital);
            // calculatedSettings.trade_size =
            //   originalTradeSize + Number(additionalTradeSize);
            calculatedSettings.number_of_grids = Math.floor(
              calculatedSettings.amount / s.trade_size,
            );
            break;

          case STRATEGIES.XENO_ARC:
            const originalCapital = Number(botDetail?.setting?.capital) || 0;
            const originalAmountArc = Number(botDetail?.setting?.amount) || 0;

            calculatedSettings.capital =
              originalCapital + Number(additionalCapital);
            // calculatedSettings.amount =
            //   originalAmountArc + Number(additionalTradeSize);
            calculatedSettings.number_of_grids = Math.floor(
              calculatedSettings.capital / s.amount,
            );
            break;

          case STRATEGIES.DCA:
            calculatedSettings.capital =
              Number(s.capital) + Number(botDetail?.setting?.capital);
            calculatedSettings.max_order_count = Math.floor(
              calculatedSettings.capital / s.amount,
            );
            break;
        }
      }

      return {
        ...prev,
        settings: {
          ...s,
          ...calculatedSettings,
        },
        exchange: {
          ...selectedExchange,
          name: selectedExchange?.name || prev.exchange_name,
          pair: selectedPair?.pair || prev.coin_name,
          base_currency: selectedPair?.base_currency,
        },
        coin_id: selectedPair?.id || prev.coin_id,
        bot_master_id: selectedBot?.id,
        userBalance,
      };
    });

    onNext();
  };

  const renderBotComponent = () => {
    const strategy = selectedBot?.bot_name?.toLowerCase();

    const initialBotTradeSize = isEdit
      ? strategy === STRATEGIES.GRID
        ? Number(botDetail?.setting?.trade_size) || 0
        : strategy === STRATEGIES.XENO_ARC
          ? Number(botDetail?.setting?.amount) || 0
          : 0
      : 0;

    const componentProps = {
      pairs,
      exchanges,
      selectedExchange,
      setSelectedExchange,
      selectedPair,
      setSelectedPair,
      selectedBot,
      userBalance: totalAvailableBalance,
      actualUserBalance: userBalance,
      initialBotCapital,
      initialBotTradeSize,

      loadingBalance,
      loadingPair: loadingPairs || (isLoading && isEdit),
      loadingExchanges: loadingExchanges,
      errors,
      setErrors,
      isEdit,
      additionalTradeSize,
      setAdditionalTradeSize,
      botDetail,
      additionalCapital,
      setAdditionalCapital,
    };
    switch (strategy) {
      case STRATEGIES.REBALANCE:
        return (
          <RebalanceComponent
            {...componentProps}
            formRebalance={formRebalance}
            setFormRebalance={setFormRebalance}
          />
        );
      case STRATEGIES.DCA:
        return (
          <DCAComponent
            {...componentProps}
            formDCA={formDCA}
            setFormDCA={setFormDCA}
          />
        );
      default:
        return (
          <XenoarcComponent
            {...componentProps}
            formXenoV2={formXenoV2}
            setFormXenoV2={setFormXenoV2}
            formXenoGrid={formXenoGrid}
            setFormXenoGrid={setFormXenoGrid}
          />
        );
    }
  };

  return (
    <Drawer open={open} onClose={onClose} width="600px">
      <div className="h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between w-full">
            <button
              className="bg-transparent hover:bg-transparent text-[#007A78] flex items-center !p-0"
              onClick={onBack}
            >
              <ChevronLeftIcon className="w-5 h-5" /> Back
            </button>
            <h2 className="text-xl font-bold">
              {isEdit ? "Edit" : "Activate"}{" "}
              {normalizeBotName(selectedBot?.bot_name)}
            </h2>
            <button
              className="bg-transparent hover:bg-transparent text-black !p-0"
              onClick={onClose}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-5 space-y-5">
            {isLoading && isEdit ? (
              <BotSettingsSkeleton />
            ) : (
              renderBotComponent()
            )}
          </div>
        </div>
        <div className="pt-8 pb-5">
          {selectedSetting === "system" &&
            Object.keys(errors || {}).length > 0 && (
              <div className="bg-[#FFE8E8] border border-[#FF6B6B] rounded-lg p-3 mb-4">
                <h4 className="text-[#B71C1C] font-semibold mb-2 text-sm tracking-wide">
                  Validation Errors
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-[#B71C1C]">
                  {Object.entries(errors).map(([key, msg]) => (
                    <li key={key}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
          <button
            type="button"
            className="w-full relative overflow-hidden !py-3 !font-bold !text-lg !bg-[#007A78]"
            disabled={
              !isFormValid ||
              !Number(totalAvailableBalance) ||
              isLoading ||
              loadingExchanges
            }
            onClick={handleNextClick}
          >
            Next
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default DrawerActivateBot2Demo;
